import { BillingInformation, PaymentMethodInfo } from "@deathstar/types/waypoint";
import { TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React from "react";
import { useWatch } from "react-hook-form";
import { classNames } from "../../classNames/classNames";
import type { Form } from "./BillingSettingsDialog";

export function PaymentMethodSelector({ form, className, readonly, disabled, billingInfo }: PaymentMethodSelector.Props): JSX.Element {
    const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<PaymentMethodInfo | null>(null);
    const paymentMethodId = useWatch({
        control: form.control,
        name: "paymentMethodId",
    });
    const fieldError = form.getFieldState("paymentMethodId")?.error;
    const options = React.useMemo(() => {
        return billingInfo?.paymentMethods.filter((pm) => pm.type === "us_bank_account");
    }, [billingInfo?.paymentMethods]);

    React.useEffect(() => {
        setSelectedPaymentMethod(billingInfo?.paymentMethods.find((paymentMethod) => paymentMethod.id === paymentMethodId) || null);
    }, [paymentMethodId, billingInfo?.paymentMethods]);

    return (
        <Autocomplete<PaymentMethodInfo, false, false, false>
            size="small"
            className={classNames(className, "lg:min-w-[300px]", {
                "pointer-events-none": readonly || disabled,
            })}
            options={options}
            value={selectedPaymentMethod || ({} as PaymentMethodInfo)}
            getOptionLabel={(option) => option.description}
            getOptionSelected={(option, value) => option.id === value?.id}
            onChange={(e, option, _reason) => {
                e.stopPropagation();
                e.preventDefault();
                form.setValue("paymentMethodId", option?.id || null);
                form.clearErrors("paymentMethodId");
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label="Automatic Payment Method"
                    error={Boolean(fieldError)}
                    helperText={
                        fieldError
                            ? fieldError.message
                            : selectedPaymentMethod
                            ? 'The "Amount Due" will be processed on the "Due Date"'
                            : null
                    }
                    inputProps={{
                        ...params.inputProps,
                        "data-lpignore": "true",
                    }}
                    disabled={disabled}
                />
            )}
        />
    );
}

export namespace PaymentMethodSelector {
    export interface Props {
        form: Form;
        billingInfo: BillingInformation;
        className?: string;
        readonly?: boolean;
        disabled?: boolean;
    }
}
